<template>
    <div class="partner-dashboard">
        <div class="container">
            <div class="header">
                <h1>🤝 合作伙伴业务大屏</h1>
                <div class="header-right">
                    <el-select v-model="selectedYear" @change="onYearChange" size="small" style="width: 120px; margin-right: 15px;">
                        <el-option label="实时" value="current" />
                        <el-option label="上一年度" value="last" />
                    </el-select>
                    <div class="time">{{ currentTime }}</div>
                </div>
            </div>

            <div class="card kpi-card" style="grid-column: span 2;">
                <div class="kpi-label">年度总消费</div>
                <div class="kpi-value">{{ yearTotal }}</div>
            </div>

            <div class="card kpi-card" style="grid-column: span 2;">
                <div class="kpi-label">本月消费</div>
                <div class="kpi-value">{{ monthTotal }}</div>
            </div>

            <div class="card kpi-card" style="grid-column: span 2;">
                <div class="kpi-label">客户数量</div>
                <div class="kpi-value">{{ customerCount }}</div>
            </div>

            <div class="card" style="grid-column: span 6;">
                <div class="card-title">📊 月度消费趋势</div>
                <div id="monthlyTrendChart" class="chart"></div>
            </div>

            <div class="card" style="grid-column: span 3;">
                <div class="card-title">👥 客户消费TOP10</div>
                <div id="customerTop10Chart" class="chart"></div>
            </div>

            <div class="card" style="grid-column: span 3;">
                <div class="card-title">📈 服务类型占比</div>
                <div id="serviceDistChart" class="chart"></div>
            </div>

            <div class="card" style="grid-column: span 4;">
                <div class="card-title">📅 季度对比分析</div>
                <div id="quarterlyChart" class="chart"></div>
            </div>

            <div class="card" style="grid-column: span 2;">
                <div class="card-title">🌍 区域分布</div>
                <div id="regionDistChart" class="chart"></div>
            </div>
        </div>
    </div>
</template>

<script>
import * as echarts from 'echarts';
import axios from 'axios';

export default {
    name: 'partnerDashboard',
    data() {
        return {
            currentTime: '',
            yearTotal: '$0',
            monthTotal: '$0',
            customerCount: 0,
            charts: {},
            selectedYear: 'current'
        }
    },
    mounted() {
        // 每年前两个月默认显示上一年度
        const now = new Date();
        if (now.getMonth() < 2) {
            this.selectedYear = 'last';
        }
        
        this.updateTime();
        setInterval(this.updateTime, 1000);
        this.initCharts();
        this.loadData();
        window.addEventListener('resize', this.resizeCharts);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.resizeCharts);
        Object.values(this.charts).forEach(chart => {
            if (chart) {
                chart.clear();
                chart.dispose();
            }
        });
    },
    methods: {
        updateTime() {
            this.currentTime = new Date().toLocaleString('zh-CN');
        },
        initCharts() {
            this.$nextTick(() => {
                this.charts.monthlyTrendChart = echarts.init(document.getElementById('monthlyTrendChart'));
                this.charts.customerTop10Chart = echarts.init(document.getElementById('customerTop10Chart'));
                this.charts.serviceDistChart = echarts.init(document.getElementById('serviceDistChart'));
                this.charts.quarterlyChart = echarts.init(document.getElementById('quarterlyChart'));
                this.charts.regionDistChart = echarts.init(document.getElementById('regionDistChart'));
            });
        },
        resizeCharts() {
            Object.values(this.charts).forEach(chart => chart && chart.resize());
        },
        onYearChange() {
            this.loadData();
        },
        async loadData() {
            const params = { year_mode: this.selectedYear };
            try {
                const [summary, monthlyTrend, customerTop10, serviceDist, quarterly, regionDist] = await Promise.all([
                    axios.get('/api/stat/partner/summary/', { params }),
                    axios.get('/api/stat/partner/monthly_trend/', { params }),
                    axios.get('/api/stat/partner/customer_top10/', { params }),
                    axios.get('/api/stat/partner/service_dist/', { params }),
                    axios.get('/api/stat/partner/quarterly/', { params }),
                    axios.get('/api/stat/partner/region_dist/', { params })
                ]);

                this.yearTotal = '$' + (summary.data.year_total / 1000000).toFixed(2) + 'M';
                this.monthTotal = '$' + (summary.data.month_total / 1000000).toFixed(2) + 'M';
                this.customerCount = summary.data.customer_count;

                this.renderMonthlyTrend(monthlyTrend.data);
                this.renderCustomerTop10(customerTop10.data);
                this.renderServiceDist(serviceDist.data);
                this.renderQuarterly(quarterly.data);
                this.renderRegionDist(regionDist.data);
            } catch (error) {
                console.error('Error loading data:', error);
                if (error.response?.status === 403) {
                    this.$message.error('无权限访问此页面');
                }
            }
        },
        renderMonthlyTrend(data) {
            if (!data || !this.charts.monthlyTrendChart) return;
            this.charts.monthlyTrendChart.clear();
            const months = data.map(d => d.month + '月');
            const amounts = data.map(d => d.amount || 0);
            
            const option = {
                grid: { left: 60, right: 30, top: 40, bottom: 30 },
                xAxis: { type: 'category', data: months, axisLabel: { color: '#fff' }, axisLine: { lineStyle: { color: '#00f2fe' } } },
                yAxis: { type: 'value', axisLabel: { color: '#00f2fe', formatter: v => '$' + (v / 1000000).toFixed(1) + 'M' }, splitLine: { lineStyle: { color: 'rgba(0, 242, 254, 0.1)' } } },
                tooltip: { trigger: 'axis', backgroundColor: 'rgba(0, 0, 0, 0.8)', borderColor: '#00f2fe' },
                series: [{
                    type: 'line',
                    data: amounts,
                    smooth: true,
                    itemStyle: { color: '#00f2fe' },
                    areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: 'rgba(0, 242, 254, 0.5)' }, { offset: 1, color: 'rgba(0, 242, 254, 0.1)' }]) }
                }]
            };
            this.charts.monthlyTrendChart.setOption(option);
        },
        renderCustomerTop10(data) {
            if (!data || !this.charts.customerTop10Chart) return;
            this.charts.customerTop10Chart.clear();
            const option = {
                grid: { left: 150, right: 60, top: 5, bottom: 30 },
                xAxis: { type: 'value', axisLabel: { color: '#00f2fe', formatter: v => '$' + (v / 1000000).toFixed(1) + 'M' }, splitLine: { lineStyle: { color: 'rgba(0, 242, 254, 0.1)' } } },
                yAxis: { type: 'category', data: data.map(d => d.customer_name).reverse(), axisLabel: { color: '#fff', fontSize: 11 }, axisLine: { lineStyle: { color: '#00f2fe' } } },
                tooltip: { trigger: 'axis', backgroundColor: 'rgba(0, 0, 0, 0.8)', borderColor: '#00f2fe' },
                series: [{
                    type: 'bar',
                    data: data.map(d => d.amount).reverse(),
                    itemStyle: { color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [{ offset: 0, color: '#00f2fe' }, { offset: 1, color: '#00ff88' }]) },
                    label: { show: true, position: 'right', color: '#fff', fontSize: 10, formatter: p => '$' + (p.value / 1000000).toFixed(1) + 'M' }
                }]
            };
            this.charts.customerTop10Chart.setOption(option);
        },
        renderServiceDist(data) {
            if (!data || !this.charts.serviceDistChart) return;
            this.charts.serviceDistChart.clear();
            const option = {
                tooltip: { backgroundColor: 'rgba(0, 0, 0, 0.8)', borderColor: '#00f2fe' },
                series: [{
                    type: 'pie',
                    radius: ['40%', '70%'],
                    data: data.map(d => ({ name: d.productCode, value: d.amount })),
                    label: { color: '#fff', fontSize: 12 },
                    itemStyle: { borderRadius: 10, borderColor: '#000', borderWidth: 2 }
                }]
            };
            this.charts.serviceDistChart.setOption(option);
        },
        renderQuarterly(data) {
            if (!data || !this.charts.quarterlyChart) return;
            this.charts.quarterlyChart.clear();
            const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
            const currentYear = new Date().getFullYear().toString();
            const lastYear = (currentYear - 1).toString();
            const currentData = quarters.map(q => {
                const item = data.find(d => d.year === currentYear && d.quarter === q);
                return item ? item.amount : 0;
            });
            const lastData = quarters.map(q => {
                const item = data.find(d => d.year === lastYear && d.quarter === q);
                return item ? item.amount : 0;
            });
            
            const option = {
                grid: { left: 60, right: 30, top: 40, bottom: 30 },
                legend: { data: [lastYear, currentYear], textStyle: { color: '#00f2fe' }, top: 5 },
                xAxis: { type: 'category', data: quarters, axisLabel: { color: '#fff' }, axisLine: { lineStyle: { color: '#00f2fe' } } },
                yAxis: { type: 'value', axisLabel: { color: '#00f2fe', formatter: v => '$' + (v / 1000000).toFixed(1) + 'M' }, splitLine: { lineStyle: { color: 'rgba(0, 242, 254, 0.1)' } } },
                tooltip: { trigger: 'axis', backgroundColor: 'rgba(0, 0, 0, 0.8)', borderColor: '#00f2fe' },
                series: [
                    { name: lastYear, type: 'bar', data: lastData, itemStyle: { color: '#f093fb' } },
                    { name: currentYear, type: 'bar', data: currentData, itemStyle: { color: '#00f2fe' } }
                ]
            };
            this.charts.quarterlyChart.setOption(option);
        },
        renderRegionDist(data) {
            if (!data || !this.charts.regionDistChart) return;
            this.charts.regionDistChart.clear();
            const option = {
                tooltip: { backgroundColor: 'rgba(0, 0, 0, 0.8)', borderColor: '#00f2fe' },
                series: [{
                    type: 'pie',
                    radius: ['40%', '70%'],
                    data: data.map(d => ({ name: d.region, value: d.amount })),
                    label: { color: '#fff', fontSize: 14, formatter: p => p.name + '\n' + p.percent + '%' },
                    itemStyle: { borderRadius: 10, borderColor: '#000', borderWidth: 3 }
                }]
            };
            this.charts.regionDistChart.setOption(option);
        }
    }
}
</script>

<style scoped>
.partner-dashboard {
    background: linear-gradient(135deg, #1a1a2e, #16213e, #0f3460);
    background-size: 400% 400%;
    animation: bgAnimation 15s ease infinite;
    height: calc(100vh - 120px);
    color: #fff;
    overflow-y: auto;
}

@keyframes bgAnimation {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
}

.container {
    width: 100%;
    padding: 10px;
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    grid-template-rows: 50px 100px 300px 280px 280px;
    gap: 8px;
}

.header {
    grid-column: 1 / 7;
    background: rgba(0, 0, 0, 0.6);
    border-radius: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 30px;
    border: 2px solid rgba(0, 255, 136, 0.3);
}

.header-right {
    display: flex;
    align-items: center;
    gap: 15px;
}

.header h1 {
    font-size: 28px;
    background: linear-gradient(90deg, #00ff88 0%, #00f2fe 50%, #00ff88 100%);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: bgAnimation 3s linear infinite;
}

.time {
    font-size: 14px;
    color: #00ff88;
}

.card {
    background: rgba(0, 0, 0, 0.6);
    border-radius: 10px;
    padding: 15px;
    border: 2px solid rgba(0, 255, 136, 0.3);
    transition: all 0.3s ease;
}

.card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 255, 136, 0.4);
}

.kpi-card {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.kpi-value {
    font-size: 32px;
    font-weight: bold;
    background: linear-gradient(90deg, #00ff88 0%, #00f2fe 50%, #4facfe 100%);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: bgAnimation 3s linear infinite;
    margin: 8px 0;
}

.kpi-label {
    font-size: 12px;
    color: #00ff88;
    text-transform: uppercase;
    letter-spacing: 1px;
}

.chart {
    width: 100%;
    height: calc(100% - 35px);
}

.card-title {
    font-size: 14px;
    margin-bottom: 10px;
    color: #00ff88;
    border-left: 3px solid #00ff88;
    padding-left: 8px;
}
</style>
